import { PrimaryLink, Text } from '@/_components';
import { CurrencyWithPrivacy } from '@/_containers';
import type { InvoiceQuerySchema } from '@/_pages/invoices/shared/schemas';

import { InvoiceDetailsTabHeader } from './invoice-details-tab-header';
import type { InvoiceAgreementLink } from './types';

type InvoiceDetailsGeneralTabProps = {
  invoice: InvoiceQuerySchema;
  agreementLinks: InvoiceAgreementLink[];
  terms: string;
  purchaseOrders: string;
};

export function InvoiceDetailsGeneralTab({
  invoice,
  agreementLinks,
  terms,
  purchaseOrders,
}: InvoiceDetailsGeneralTabProps) {
  const tenderSubtotal = invoice.subtotal_amount ?? invoice.invoice_amount;

  return (
    <div className="space-y-2 px-2 py-2">
      <InvoiceDetailsTabHeader invoice={invoice} />

      <div>
        <Text className="text-c-card-muted text-[10px] font-semibold tracking-wide uppercase">Agreement #</Text>
        <div className="mt-1 flex flex-wrap gap-1">
          {agreementLinks.length > 0 ? (
            agreementLinks.map((agreement) => (
              <PrimaryLink
                key={agreement.id}
                to="/agreements/$id"
                params={{ id: String(agreement.id) }}
                state={{ naturalBack: true }}
                target="_blank"
                rel="noopener noreferrer"
                containerClassName="border-c-border-muted text-c-card-foreground hover:bg-c-background inline-flex rounded border px-2 py-0.5 text-xs"
                className="text-c-card-foreground"
              >
                {agreement.agreementId}
              </PrimaryLink>
            ))
          ) : (
            <Text className="text-c-card-muted text-xs">—</Text>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:items-start">
        <div className="border-c-border rounded border p-2">
          <Text className="text-c-card-muted mb-1 text-[10px] font-semibold tracking-wide uppercase">Header</Text>
          <div className="space-y-1">
            <div className="grid grid-cols-[3.5rem_1fr] items-start gap-1">
              <Text className="text-c-card-muted text-xs">Terms:</Text>
              <Text className="text-c-card-foreground text-xs font-medium">{terms || '—'}</Text>
            </div>
            <div className="grid grid-cols-[3.5rem_1fr] items-start gap-1">
              <Text className="text-c-card-muted text-xs">PO:</Text>
              <Text className="text-c-card-foreground text-xs font-medium">{purchaseOrders || '—'}</Text>
            </div>
          </div>
        </div>

        <div className="border-c-border rounded border">
          <div className="border-c-border-muted grid grid-cols-2 border-b px-2 py-1">
            <Text className="text-c-card-muted text-[10px] font-semibold tracking-wide uppercase">Tender</Text>
            <Text className="text-c-card-muted text-right text-[10px] font-semibold tracking-wide uppercase">
              Amount
            </Text>
          </div>
          <div className="space-y-1 px-2 py-1.5">
            <div className="flex items-center justify-between gap-2">
              <Text className="text-c-card-foreground text-xs">On Account</Text>
              <Text className="text-c-card-muted text-xs">
                {invoice.on_account == null ? '—' : <CurrencyWithPrivacy value={invoice.on_account} />}
              </Text>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Text className="text-c-card-foreground text-xs">SubTotal</Text>
              <Text className="text-c-card-muted text-xs">
                {tenderSubtotal == null ? '—' : <CurrencyWithPrivacy value={tenderSubtotal} />}
              </Text>
            </div>
            <div className="flex items-center justify-between gap-2">
              <Text className="text-c-card-foreground text-xs">Exempt</Text>
              <Text className="text-c-card-muted text-xs">
                {invoice.exempt == null ? '—' : <CurrencyWithPrivacy value={invoice.exempt} />}
              </Text>
            </div>
            <div className="border-c-border-muted flex items-center justify-between gap-2 border-t pt-1">
              <Text className="text-c-card-foreground text-xs font-semibold">Total</Text>
              <Text className="text-c-card-foreground text-xs font-semibold">
                {invoice.invoice_amount == null ? '—' : <CurrencyWithPrivacy value={invoice.invoice_amount} />}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
